import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuthState } from "@/hooks/useAuthState";
import { AssessmentCard } from "@/features/LegacyBenchmarksUI/components/landing/AssessmentCard";
import { ROUTES } from "@/config/routes";

const Landing = () => {
  const { user } = useAuthState();
  const navigate = useNavigate();
  const [hasReadinessAssessment, setHasReadinessAssessment] = useState(false);
  const [hasOpportunityAssessment, setHasOpportunityAssessment] = useState(false);
  const [hasBenchmarkingAssessment, setHasBenchmarkingAssessment] = useState(false);

  useEffect(() => {
    const fetchUserAssessments = async () => {
      if (!user) {
        setHasReadinessAssessment(false);
        setHasOpportunityAssessment(false);
        setHasBenchmarkingAssessment(false);
        return;
      }

      try {
        console.log('Fetching assessments for user:', user.id);
        
        // Check for opportunity assessment
        const { data: opportunityData, error: opportunityError } = await supabase
          .from('assessments')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        if (opportunityError) {
          console.error('Error fetching opportunity assessment:', opportunityError);
        }
        console.log('Opportunity assessment data:', opportunityData);
        setHasOpportunityAssessment(!!opportunityData);

        // Check for readiness assessment
        const { data: readinessData, error: readinessError } = await supabase
          .from('readiness_assessments')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        if (readinessError) {
          console.error('Error fetching readiness assessment:', readinessError);
        }
        console.log('Readiness assessment data:', readinessData);
        setHasReadinessAssessment(!!readinessData);

        // Check for benchmarking assessment
        const { data: benchmarkingData, error: benchmarkingError } = await supabase
          .from('process_assessments')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        if (benchmarkingError) {
          console.error('Error fetching benchmarking assessment:', benchmarkingError);
        }
        console.log('Benchmarking assessment data:', benchmarkingData);
        setHasBenchmarkingAssessment(!!benchmarkingData);

      } catch (error) {
        console.error('Error fetching assessments:', error);
      }
    };

    fetchUserAssessments();
  }, [user]);

  const handleOpportunityClick = () => {
    if (hasOpportunityAssessment) {
      navigate(ROUTES.OPPORTUNITY_ASSESSMENT.ROOT);
    } else {
      navigate(ROUTES.OPPORTUNITY_ASSESSMENT.WIZARD);
    }
  };

  const handleReadinessClick = () => {
    if (hasReadinessAssessment) {
      navigate(ROUTES.READINESS_ASSESSMENT.ROOT);
    } else {
      navigate(ROUTES.READINESS_ASSESSMENT.WIZARD);
    }
  };

  const handleBenchmarkingClick = () => {
    navigate(ROUTES.PERMIT_BENCHMARKING.ROOT);
  };

  return (
    <div className="py-16 pt-0">
      <div className="container mx-auto px-4">
        <div className="text-center mb-6">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            AI Readiness Assessment
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Evaluate your jurisdiction's readiness for AI adoption and identify the
            most impactful opportunities
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <AssessmentCard
            title="Opportunity Assessment"
            description="Identify and prioritize AI opportunities based on value and feasibility"
            hasAssessment={hasOpportunityAssessment}
            viewPath={ROUTES.OPPORTUNITY_ASSESSMENT.ROOT}
            startPath={ROUTES.OPPORTUNITY_ASSESSMENT.WIZARD}
            onClick={handleOpportunityClick}
          />

          <AssessmentCard
            title="Readiness Assessment"
            description="Evaluate your organization's readiness for AI adoption across key dimensions"
            hasAssessment={hasReadinessAssessment}
            viewPath={ROUTES.READINESS_ASSESSMENT.ROOT}
            startPath={ROUTES.READINESS_ASSESSMENT.WIZARD}
            onClick={handleReadinessClick}
          />

          <AssessmentCard
            title="Process Assessment"
            description="Benchmark your permitting process performance against peers"
            hasAssessment={false}
            viewPath=""
            startPath=""
            isComingSoon
          />

          <AssessmentCard
            title="Permit Benchmarking"
            description="Compare your permitting process performance against peer jurisdictions"
            hasAssessment={hasBenchmarkingAssessment}
            viewPath={ROUTES.PERMIT_BENCHMARKING.ROOT}
            startPath={ROUTES.PERMIT_BENCHMARKING.WIZARD}
            onClick={handleBenchmarkingClick}
            customButtonText="View Benchmarks"
          />
        </div>
      </div>
    </div>
  );
};

export default Landing;